if (!customElements.get('byte-brand-grid')) {
  class ByteBrandGrid extends HTMLElement {
    constructor() {
      super();

      this.isSliderEnabled = false;
      this.boundHandleResize = this.handleResize.bind(this);
      this.boundHandlePrev = this.handlePrev.bind(this);
      this.boundHandleNext = this.handleNext.bind(this);
      this.boundHandleScroll = this.handleScroll.bind(this);
      this.boundHandleMousedown = this.handleMousedown.bind(this);
      this.boundHandleMouseup = this.handleMouseup.bind(this);
      this.boundHandleMousemove = this.handleMousemove.bind(this);
      this.boundHandleClick = this.handleClick.bind(this);
    }

    connectedCallback() {
      if (!this.hasAttribute('data-slider')) return;

      this.viewport = this.querySelector('[data-brand-grid-viewport]');
      this.track = this.querySelector('[data-brand-grid-track]');
      this.items = [...this.querySelectorAll('[data-brand-grid-item]')];
      this.prevButton = this.closest('.shopify-section--byte-brand-grid')?.querySelector('[data-brand-grid-action="prev"]');
      this.nextButton = this.closest('.shopify-section--byte-brand-grid')?.querySelector('[data-brand-grid-action="next"]');

      this.mediaQuery = window.matchMedia('(min-width: 750px)');
      this.#updateSliderState();
      this.#bindEvents();
    }

    disconnectedCallback() {
      this.#unbindEvents();
    }

    handleResize() {
      this.#updateSliderState();
      this.#updateNavState();
    }

    handleScroll() {
      this.#updateNavState();
    }

    handlePrev() {
      if (!this.isSliderEnabled) return;

      const scrollAmount = this.#getScrollAmount();
      this.viewport.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
    }

    handleNext() {
      if (!this.isSliderEnabled) return;

      const scrollAmount = this.#getScrollAmount();
      this.viewport.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }

    handleMousedown(event) {
      if (!this.isSliderEnabled) return;

      this.isMouseDown = true;
      this.isMouseMoved = false;
      this.dragStartX = event.pageX;
      this.scrollStart = this.viewport.scrollLeft;
      this.viewport.classList.add('is-grabbing');
    }

    handleMouseup() {
      if (!this.isSliderEnabled) return;

      this.isMouseDown = false;
      this.viewport.classList.remove('is-grabbing');
    }

    handleMousemove(event) {
      if (!this.isSliderEnabled || !this.isMouseDown) return;

      event.preventDefault();

      const deltaX = event.pageX - this.dragStartX;
      this.viewport.scrollLeft = this.scrollStart - deltaX;

      if (Math.abs(deltaX) > 8) {
        this.isMouseMoved = true;
      }
    }

    handleClick(event) {
      if (!this.isSliderEnabled || !this.isMouseMoved) return;

      event.preventDefault();
      event.stopPropagation();
      this.isMouseMoved = false;
    }

    #getScrollAmount() {
      const firstItem = this.items[0];

      if (!firstItem) return 0;

      const secondItem = this.items[1];

      if (secondItem) {
        return secondItem.offsetLeft - firstItem.offsetLeft;
      }

      return firstItem.clientWidth + parseFloat(getComputedStyle(this.track).columnGap || getComputedStyle(this.track).gap || 0);
    }

    #updateSliderState() {
      const isDesktop = this.mediaQuery.matches;
      const itemCount = this.items.length;
      const isDesktopSlider = isDesktop && itemCount > 3;
      const isMobileSlider = !isDesktop && itemCount > 2;

      this.isSliderEnabled = isDesktopSlider || isMobileSlider;
      this.classList.toggle('is-slider-active', isDesktopSlider);
      this.classList.toggle('is-slider-active-mobile', isMobileSlider);

      if (this.isSliderEnabled) {
        this.viewport.classList.toggle('is-grabbable', window.matchMedia('(pointer: fine)').matches);
        this.#updateNavState();
      } else {
        this.viewport.classList.remove('is-grabbable', 'is-grabbing');
        if (this.prevButton) this.prevButton.disabled = true;
        if (this.nextButton) this.nextButton.disabled = true;
      }
    }

    #updateNavState() {
      if (!this.isSliderEnabled || !this.viewport) return;

      const maxScroll = this.viewport.scrollWidth - this.viewport.clientWidth;
      const scrollLeft = this.viewport.scrollLeft;
      const isAtStart = scrollLeft <= 1;
      const isAtEnd = scrollLeft >= maxScroll - 1;

      if (this.prevButton) this.prevButton.disabled = isAtStart;
      if (this.nextButton) this.nextButton.disabled = isAtEnd;
    }

    #bindEvents() {
      this.mediaQuery.addEventListener('change', this.boundHandleResize);
      window.addEventListener('on:debounced-resize', this.boundHandleResize);

      if (this.viewport) {
        this.viewport.addEventListener('scroll', this.boundHandleScroll);
        this.viewport.addEventListener('mousedown', this.boundHandleMousedown);
        this.viewport.addEventListener('mouseup', this.boundHandleMouseup);
        this.viewport.addEventListener('mouseleave', this.boundHandleMouseup);
        this.viewport.addEventListener('mousemove', this.boundHandleMousemove);
        this.viewport.addEventListener('click', this.boundHandleClick, true);
        this.viewport.addEventListener('dragstart', (event) => event.preventDefault());
      }

      if (this.prevButton) this.prevButton.addEventListener('click', this.boundHandlePrev);
      if (this.nextButton) this.nextButton.addEventListener('click', this.boundHandleNext);
    }

    #unbindEvents() {
      this.mediaQuery?.removeEventListener('change', this.boundHandleResize);
      window.removeEventListener('on:debounced-resize', this.boundHandleResize);

      if (this.viewport) {
        this.viewport.removeEventListener('scroll', this.boundHandleScroll);
        this.viewport.removeEventListener('mousedown', this.boundHandleMousedown);
        this.viewport.removeEventListener('mouseup', this.boundHandleMouseup);
        this.viewport.removeEventListener('mouseleave', this.boundHandleMouseup);
        this.viewport.removeEventListener('mousemove', this.boundHandleMousemove);
        this.viewport.removeEventListener('click', this.boundHandleClick, true);
      }

      if (this.prevButton) this.prevButton.removeEventListener('click', this.boundHandlePrev);
      if (this.nextButton) this.nextButton.removeEventListener('click', this.boundHandleNext);
    }
  }

  customElements.define('byte-brand-grid', ByteBrandGrid);
}
